import { useMemo } from 'react';

import {
  useRefreshTauriAudioDevices,
  useSelectedTauriInputDeviceName,
} from '@/hooks/useAudioDeviceSelector';
// eslint-disable-next-line @heidi/rules/no-new-rematch-coupling -- the two Rematch WRITES below (auth region pin, session-mic selection) have no hook accessor to reuse; this module IS the ruling-4 composition binding and every READ already crosses through the grandfathered accessors above
import { useDispatch, useStore } from 'react-redux';

import type { OnboardingStoreAccess } from '@heidi/dictate';
// eslint-disable-next-line @heidi/rules/no-new-rematch-coupling -- type-only and erases at run time; the Dispatch/RootState shapes are the store's own and have no hook-side twin
import { type Dispatch, type RootState } from '@/store';
import { SYSTEM_DEFAULT_DEVICE_MARKER } from '@/tauri/constants';
import { extractRegionFromAccessToken } from '@heidi/auth/tokens';
import { type Region } from '@heidi/schema/ml-scribe';

/**
 * Ruling 4: narrow reactive reads (session mic name only); the device list and
 * auth token are read at event time. The steps take these as props and carry
 * no store imports.
 */
export function useDictateOnboardingStoreAccess(): OnboardingStoreAccess {
  const dispatch = useDispatch<Dispatch>();
  const store = useStore<RootState>();
  const sessionDeviceName = useSelectedTauriInputDeviceName();
  const refreshDevices = useRefreshTauriAudioDevices();

  return useMemo(
    () => ({
      aboutYou: {
        onSetUserRegion: (region: Region) => {
          dispatch.auth.setUserRegion(region);
        },
        getTokenRegion: () => {
          const token = store.getState().auth.token;
          return token ? extractRegionFromAccessToken(token) : undefined;
        },
      },
      mic: {
        sessionDeviceName,
        onLoadDevices: refreshDevices,
        onSelectSessionDevice: (deviceName: string | null) => {
          if (deviceName === null) {
            dispatch.scribeRecordModel.setTauriInputDevice({
              name: SYSTEM_DEFAULT_DEVICE_MARKER,
              is_default: true,
              display_name: null,
              icon_data_url: null,
              is_communications_default: false,
            });
            return;
          }
          const selected = store
            .getState()
            .scribeRecordModel.tauriInputDevices?.find(
              (item) => item.name === deviceName
            );
          if (selected) dispatch.scribeRecordModel.setTauriInputDevice(selected);
        },
      },
    }),
    [dispatch, refreshDevices, sessionDeviceName, store]
  );
}
